/**
 * Owns references to consumer-provided light-DOM nodes while components
 * temporarily move them into shadow DOM for teleported dropdown content.
 */
export class LightDomReparentController {
    /**
     * @param {HTMLElement} host
     */
    constructor(host) {
        this.host = host;
        /** @type {Map<string, Set<Element>>} */
        this._buckets = new Map();
        /** @type {Array<{bucket: string, type: string, listener: EventListener}>} */
        this._listenerDefinitions = [];
        /** @type {Map<Element, AbortController>} */
        this._listenerControllers = new Map();
        this._connected = false;
    }

    connect() {
        if (this._connected) {
            return;
        }

        this._connected = true;
        this._syncListeners();
    }

    disconnect({ restore = true } = {}) {
        this._connected = false;
        this._abortListeners();

        if (restore) {
            this.restoreAll();
        }
    }

    /**
     * Records nodes exposed by a slot. Empty slotchange notifications caused
     * by an internal move intentionally do not clear the bucket.
     *
     * @param {string} bucket
     * @param {Iterable<Element>} nodes
     */
    capture(bucket, nodes) {
        const elements = Array.from(nodes || []).filter((node) => node instanceof Element);
        if (!elements.length) {
            return;
        }

        const stored = this._buckets.get(bucket) || new Set();
        elements.forEach((node) => stored.add(node));
        this._buckets.set(bucket, stored);
        this._syncListeners();
    }

    /**
     * @param {string} bucket
     * @returns {Element[]}
     */
    get(bucket) {
        const nodes = this._buckets.get(bucket);
        if (!nodes) {
            return [];
        }

        for (const node of nodes) {
            if (!node.parentNode && this.host.isConnected) {
                nodes.delete(node);
                this._listenerControllers.get(node)?.abort();
                this._listenerControllers.delete(node);
            }
        }

        return Array.from(nodes);
    }

    /**
     * @param {string} bucket
     * @returns {Element|null}
     */
    getFirst(bucket) {
        return this.get(bucket)[0] || null;
    }

    /**
     * Moves all bucket nodes to a stable target without cloning form controls.
     *
     * @param {string} bucket
     * @param {Element|null|undefined} target
     */
    move(bucket, target) {
        if (!target) {
            return;
        }

        this.get(bucket).forEach((node) => {
            if (node.parentNode !== target) {
                target.appendChild(node);
            }
        });
    }

    restore(bucket) {
        this.move(bucket, this.host);
    }

    restoreAll() {
        this._buckets.forEach((_nodes, bucket) => this.restore(bucket));
    }

    /**
     * Registers listeners for current captured nodes. Re-entrant: previous
     * subscriptions are aborted so Apply/Reset cannot fire more than once.
     *
     * @param {string} bucket
     * @param {string} type
     * @param {EventListener} listener
     */
    listen(bucket, type, listener) {
        this._listenerDefinitions.push({ bucket, type, listener });
        this._syncListeners();
    }

    _syncListeners() {
        if (!this._connected) {
            return;
        }

        this._abortListeners();

        this._listenerDefinitions.forEach(({ bucket, type, listener }) => {
            this.get(bucket).forEach((node) => {
                let controller = this._listenerControllers.get(node);
                if (!controller) {
                    controller = new AbortController();
                    this._listenerControllers.set(node, controller);
                }

                node.addEventListener(type, listener, {
                    signal: controller.signal,
                });
            });
        });
    }

    _abortListeners() {
        this._listenerControllers.forEach((controller) => controller.abort());
        this._listenerControllers.clear();
    }
}
